import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { useCompany } from "../context/CompanyContext";

export default function Login() {
  const { company, logoDark } = useCompany();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true); // default ON — most users expect it
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirectTo = params.get("redirect");

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(identifier, password, { rememberMe });
      toast.success(`Bem-vindo, ${u.name}`);
      // Honour ?redirect=… coming from the magic-link fallback,
      // but only when the path is internal and matches the user's role.
      const safe = redirectTo && redirectTo.startsWith("/") && !redirectTo.startsWith("//")
        ? redirectTo : null;
      if (safe && (
        (u.role === "admin" && safe.startsWith("/admin")) ||
        (u.role === "employee" && safe.startsWith("/funcionario")) ||
        (u.role === "client" && safe.startsWith("/cliente"))
      )) {
        nav(safe);
      } else if (u.role === "admin") nav("/admin");
      else if (u.role === "employee") nav("/funcionario");
      else nav("/cliente");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative bg-[#062c27]">
        <img alt="" src="https://images.pexels.com/photos/6196239/pexels-photo-6196239.jpeg"
             className="absolute inset-0 w-full h-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#041f1b]/60 to-[#041f1b]" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="inline-block" data-testid="brand-link">
            <span className="bg-[#fdfbf7] rounded-sm px-3 py-2 inline-flex items-center">
              <img src={logoDark} alt={company.trade_name} className="h-16 w-auto" />
            </span>
          </Link>
          <div>
            <div className="eyebrow mb-4">Gestão discreta. Resultado visível.</div>
            <h1 className="font-display text-5xl leading-tight">Entre na sua<br/>área privada.</h1>
          </div>
        </div>
      </div>

      <div className="p-8 md:p-16 flex items-center">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <Link to="/"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37] transition-colors -mb-2"
                data-testid="login-back-home">
            <ArrowLeft size={14} /> Voltar à página inicial
          </Link>
          <div>
            <div className="eyebrow mb-2">Autenticação</div>
            <h2 className="font-display text-4xl">Entrar</h2>
          </div>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Email ou utilizador</label>
            <input type="text" required autoComplete="username" value={identifier}
                   onChange={e => setIdentifier(e.target.value)}
                   placeholder="exemplo@luxstay.pt ou o seu username"
                   inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                   className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 text-base outline-none focus:border-[#d4af37] placeholder:text-[#1a4640]"
                   data-testid="login-email" />
            <div className="text-[11px] text-[#a0b2af]">
              Entre com o <span className="text-[#d4af37]">email</span> ou, se tiver definido, com o <span className="text-[#d4af37]">nome de utilizador</span>.
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Palavra-passe</label>
            <input type="password" required value={password} onChange={e => setPassword(e.target.value)}
                   autoComplete="current-password"
                   className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 text-base outline-none focus:border-[#d4af37]"
                   data-testid="login-password" />
          </div>
          <button disabled={busy} className="btn-gold w-full py-4 text-base disabled:opacity-60" data-testid="login-submit">
            {busy ? "A entrar…" : "Entrar"}
          </button>
          <label className="flex items-center gap-3 text-sm text-[#a0b2af] cursor-pointer select-none"
                 data-testid="login-remember-label">
            <input type="checkbox" checked={rememberMe}
                   onChange={e => setRememberMe(e.target.checked)}
                   className="w-4 h-4 accent-[#d4af37] cursor-pointer"
                   data-testid="login-remember-me" />
            <span>
              Manter sessão ativa
              <span className="block text-[10px] text-[#a0b2af]/70 mt-0.5">
                Não recomendamos em computadores partilhados.
              </span>
            </span>
          </label>
          <div className="flex items-center justify-between text-sm">
            <Link to="/recuperar-password" className="text-[#a0b2af] hover:text-[#d4af37] hover:underline"
                  data-testid="link-forgot-password">
              Esqueci a palavra-passe
            </Link>
            <Link to="/registo" className="text-[#d4af37] hover:underline" data-testid="link-register">
              Criar conta
            </Link>
          </div>
          <div className="text-xs text-[#a0b2af]">
            Ainda não tem conta? Toque em <span className="text-[#d4af37]">Criar conta</span>.
          </div>
        </form>
      </div>
    </div>
  );
}
